import { client, fileUploadClient, fileDownloadClient } from "./axiosClient";

/**
 * Carrega o snapshot V3 + estado de runtime da implementação atual.
 *
 * O backend resolve a implementação pelo ``request.user.current_implementation``
 * (token JWT / session), então não há necessidade de passar ``app_name`` aqui.
 *
 * Shape esperado:
 *   {
 *     period: { year, month, label_key },
 *     implementation: { app_name, display_name },
 *     snapshot: { schema_version, inputs, input_groups, blocks, cross_links, ... },
 *     runtime: {
 *       inputs: { [meta_name]: { attention_level, attention_label, state_kind,
 *                                state_params, is_permanent_for_month,
 *                                responsible_user } },
 *       current_user: { name, email }
 *     }
 *   }
 */
/**
 * Sumario agregado de pendencias (decisoes + inputs nao enviados)
 * consumido pelo sino global (``DecisionsToolbarItem``) e pelo marcador
 * de meses no ``MonthSelectorV3``.
 *
 * Shape:
 *   { decisions_count, missing_inputs_count, total, months_with_pending: [] }
 *
 * Degrada silenciosamente (throw -> consumidor trata como zero).
 */
export async function fetchInputsV3PendingSummary({ signal } = {}) {
  const response = await client.get("api/v3/pending-summary/", { signal });
  return response.data;
}

export async function fetchInputsV3Maintenance() {
  const response = await client.get("api/v3/maintenance/");
  return response.data;
}

export async function updateInputsV3Maintenance({
  action,
  reason = "",
  ttl_choice,
}) {
  const payload = { action, reason };
  if (ttl_choice) payload.ttl_choice = ttl_choice;
  const response = await client.post("api/v3/maintenance/", payload);
  return response.data;
}

/**
 * Lista as implementacoes que o usuario logado mantem em manutencao
 * (claim ativa). Consumido pelo badge global do icone de manutencao —
 * se a lista nao esta vazia, o user tem trabalho pendente em alguma
 * impl, mesmo que esteja vendo outra agora. Degrada silenciosamente.
 *
 * Shape: { success, claims: [ { implementation_id, implementation_app,
 *   ttl_choice, ttl_hours, expires_at, activated_at } ] }
 */
export async function fetchInputsV3MyClaims() {
  const response = await client.get("api/v3/maintenance/my-claims/");
  return response.data;
}

/**
 * Payload unico da floating toolbar V3. Substitui as leituras separadas de
 * pending-summary, maintenance e my-claims nos icones globais.
 *
 * Shape: { success, pending_summary, maintenance, claims }
 */
export async function fetchInputsV3ToolbarSummary() {
  const response = await client.get("api/v3/toolbar-summary/");
  return response.data;
}

/**
 * Endpoint UNIFICADO do cockpit V3 (Onda 2.0 mai/2026).
 *
 * Backend: ``InputsV3StateView`` em ``GET /api/v3/inputs/state/``.
 * Substitui o trio ``fetchInputsV3Snapshot`` + ``fetchBlocksV3`` +
 * ``fetchInputDrawerDetailsV3``. Resolve o problema de fontes de
 * verdade divergentes (3 pollings com timings distintos expondo
 * estado inconsistente: badge "acao necessaria" + cards "shimmer" +
 * timeline drawer "iniciando" todos no mesmo instante).
 *
 * Shape:
 *   {
 *     runtime: {
 *       period, month_status, month_status_reason, month_status_active,
 *       implementation, snapshot, runtime: { inputs, current_user, ... }
 *     },
 *     blocks: { blocks, summary, snapshotSource, userIsDev },
 *     drawer_details: { about, control, runtime_input, ... } | null
 *   }
 *
 * ETag/304 igual aos demais polling endpoints (PA-009): quando o
 * cliente passa ``etag`` previo, o backend devolve 304 sem corpo se
 * nada mudou no escopo (impl, year, month, drawer).
 */
export async function fetchInputsStateV3({
  year,
  month,
  drawerMeta = null,
  etag = null,
  signal,
} = {}) {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  if (drawerMeta) params.drawer = drawerMeta;
  const headers = {};
  if (etag) headers["If-None-Match"] = etag;
  const response = await client.get("api/v3/inputs/state/", {
    params,
    headers,
    signal,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
  });
  if (response.status === 304) {
    return {
      data: null,
      etag: response.headers?.etag || etag || null,
      notModified: true,
    };
  }
  return {
    data: response.data,
    etag: response.headers?.etag || null,
    notModified: false,
  };
}

export async function fetchInputsV3Snapshot({ year, month, signal } = {}) {
  // ``year``/``month`` opcionais: quando presentes, o backend resolve
  // o periodo via query params (``_resolve_period`` no
  // ``InputsV3SnapshotView``) em vez de cair no fallback
  // ``latest PipelineControl``/UTC. Isso e o que permite trocar o mes
  // "foco" no seletor sem que o backend sempre devolva o mais recente.
  //
  // ``signal``: AbortSignal opcional. Quando o consumidor (InputsV3.js)
  // troca o mes selecionado durante um fetch em curso, o controller
  // anterior e' abortado — o axios cancela a request via ``CanceledError``
  // em vez de o frontend ter que descartar a resposta tardia. Tambem
  // protege contra setState em componente desmontado.
  const params = {};
  if (year && month) {
    params.year = year;
    params.month = month;
  }
  const response = await client.get("api/v3/snapshot/", { params, signal });
  return response.data;
}

/**
 * Envia o arquivo bruto de um input V3 ao backend.
 *
 * Ao contrário do ``api/inputs_upload/`` V2 (que recebe um ``input_control_id``
 * numérico via FormData e ainda dispara pipeline de cálculo), o endpoint V3 é
 * puramente de escrita "estado 1: arquivo recebido". O parse/cross-linking/
 * calculation é acoplado em uma fase posterior (estágio 4 do D-19).
 *
 * @param {object} params
 * @param {string} params.appName   ``current_implementation.app_name`` do user.
 * @param {number} params.year      Ano do período (1..).
 * @param {number} params.month     Mês do período (1..12).
 * @param {string} params.metaName  ``meta_name`` estável do input (V3).
 * @param {File}   params.file      Arquivo selecionado pelo usuário.
 * @returns {Promise<object>}       Shape:
 *   ``{ success, meta_name, state, delivered_at, file_path, original_filename }``
 *
 * Códigos de erro que o componente pode exibir amigavelmente:
 *   - ``missing_file`` / ``file_too_large`` / ``invalid_period``
 *   - ``input_not_found`` / ``implementation_mismatch`` / ``inputs_v3_not_enabled``
 */
export async function uploadInputV3({
  appName,
  year,
  month,
  metaName,
  file,
  onProgress,
}) {
  const formData = new FormData();
  formData.append("file", file);
  // ``onProgress(pct)`` opcional para barras de progresso "verdadeiras"
  // por arquivo (ex.: multi-month upload). Se passado, sobrescreve o
  // ``onUploadProgress`` global do ``fileUploadClient`` (que dispara um
  // setProgress no redux store usado pela barra global do app). Como
  // axios prefere o ``config.onUploadProgress`` sobre o do instance,
  // basta passar aqui — sem efeito colateral no upload "comum".
  const config = {};
  if (typeof onProgress === "function") {
    config.onUploadProgress = (progressEvent) => {
      if (!progressEvent || !progressEvent.total) return;
      const pct = Math.round(
        (100 * progressEvent.loaded) / progressEvent.total,
      );
      onProgress(Math.min(99, pct));
    };
  }
  const response = await fileUploadClient.post(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/upload/`,
    formData,
    config,
  );
  return response.data;
}

/**
 * Upload do MESMO arquivo aplicado em N meses (modo "Unico arquivo"
 * do modal "Enviar multiplos meses").
 *
 * Faz UMA unica chamada HTTP carregando o arquivo + um array de
 * periodos; o backend (``InputsV3UploadMultiView``) le o arquivo
 * uma vez em memoria e replica para cada periodo, criando
 * ``InputControlV3`` independentes (cada um com blob proprio).
 *
 * @param {object} p
 * @param {string} p.appName
 * @param {string} p.metaName
 * @param {File}   p.file
 * @param {Array<{year:number,month:number}>} p.periods
 * @param {(pct:number)=>void} [p.onProgress] Progresso do upload
 *   HTTP (0-99). NAO reflete o progresso do parse de cada periodo
 *   — apenas a subida do arquivo. Como existe so 1 upload fisico,
 *   a barra representa a transferencia em si.
 *
 * @returns {Promise<{
 *   success: boolean,
 *   meta_name: string,
 *   total: number,
 *   results: Array<{
 *     year: number, month: number, success: boolean,
 *     state: string, delivered_at: string,
 *     job_id: string|null, processing_deferred: boolean
 *   }>
 * }>}
 */
export async function uploadInputBroadcastV3({
  appName,
  metaName,
  file,
  periods,
  onProgress,
}) {
  const formData = new FormData();
  formData.append("file", file);
  // ``periods`` viaja como string JSON dentro do multipart porque o
  // multipart/form-data nao tem nocao nativa de array de objetos.
  // O backend faz ``json.loads`` no recebimento. Mesmo padrao do
  // wagon ``additional_*`` dos endpoints V2.
  formData.append("periods", JSON.stringify(periods || []));
  const config = {};
  if (typeof onProgress === "function") {
    config.onUploadProgress = (progressEvent) => {
      if (!progressEvent || !progressEvent.total) return;
      const pct = Math.round(
        (100 * progressEvent.loaded) / progressEvent.total,
      );
      onProgress(Math.min(99, pct));
    };
  }
  const response = await fileUploadClient.post(
    `api/v3/inputs/${encodeURIComponent(appName)}/${encodeURIComponent(
      metaName,
    )}/upload-multi/`,
    formData,
    config,
  );
  return response.data;
}

/**
 * Lista os meses (year, month) que ja tem arquivo entregue para um
 * input no calendario da implementacao corrente.
 *
 * Consumido pelo modal "Enviar multiplos meses" (botao ao lado de
 * "Enviar arquivo" no drawer): a UI marca esses meses com aviso
 * visual de "sera substituido".
 *
 * Backend: ``GET /api/v3/inputs/<app>/<meta_name>/delivered-months/``.
 *
 * Resposta:
 *   { success, meta_name, volatility,
 *     delivered: [{year, month, state}, ...] }
 */
export async function fetchInputDeliveredMonthsV3({ appName, metaName }) {
  const response = await client.get(
    `api/v3/inputs/${encodeURIComponent(appName)}/${encodeURIComponent(
      metaName,
    )}/delivered-months/`,
  );
  return response.data;
}

/**
 * Pareamento sugerido de nomes de arquivos para meses, baseado em
 * heuristicas semanticas + LLM auxiliar. Reusa a infra ja exposta
 * pelo backend para o ``SpecialOptionsModal`` da V2.
 *
 * Backend: ``POST /api/chat/file-month-match/``.
 *
 * @param {object} params
 * @param {string[]} params.filenames     Nomes (so o ``file.name`` basta).
 * @param {string[]} params.months        Labels dos meses candidatos
 *   no formato "MMM-YY" (mesma string que aparece no MonthSelectorV3,
 *   ex.: "Jan-24"). Veja ``buildMonthLabelsForMatch`` no hook.
 * @param {string} [params.inputName]     Nome do input (pista contextual
 *   para o backend; opcional, melhora qualidade do match).
 * @returns {Promise<{ matches: Array<{
 *   filename: string,
 *   month: string | null,
 *   confidence: "high" | "medium" | "none"
 * }> }>}
 */
export async function matchFilenamesToMonthsV3({
  filenames,
  months,
  inputName = "",
}) {
  const response = await client.post("api/chat/file-month-match/", {
    filenames,
    months,
    input_name: inputName,
  });
  return response.data;
}

/**
 * Reusa a entrega do periodo anterior para o periodo corrente (V3).
 *
 * Acao: o backend busca o ``InputControlV3`` com ``state='success'`` mais
 * recente em periodos estritamente ANTERIORES, copia o ``file`` para o
 * periodo atual criando um novo upload, e dispara o pipeline.
 *
 * Quando nao ha entrega anterior elegivel, responde com
 * ``error_code: 'input_not_replicable'`` e status 404 — o componente
 * chamador pode ignorar silenciosamente (o botao ja deveria estar
 * desabilitado nesse cenario via ``canReuse``).
 *
 * @returns {Promise<object>} Mesmo shape de ``uploadInputV3`` + campo
 *   ``replicated_from: { year, month }`` informando de qual periodo a
 *   copia veio.
 */
export async function replicateInputV3({ appName, year, month, metaName }) {
  const response = await client.post(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/replicate/`,
  );
  return response.data;
}

/**
 * Re-executa o pipeline para um input que ja tem arquivo no mes (sem
 * trocar o arquivo). Marca o ``InputControlV3`` como ``stale`` +
 * cascade transitiva em xlinks e blocos dependentes. Acionado pelo
 * botao "Reprocessar" do drawer (DEV-only).
 *
 * @returns {Promise<{
 *   success: boolean,
 *   meta_name: string,
 *   year: number,
 *   month: number,
 *   job_id: string,
 *   cascade: {
 *     impacted_blocks: string[],
 *     impacted_xlinks: string[],
 *     blocks_marked_stale: number,
 *     xlinks_marked_stale: number,
 *   },
 * }>}
 */
export async function reprocessInputV3({ appName, year, month, metaName }) {
  const response = await client.post(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/reprocess/`,
  );
  return response.data;
}

/**
 * Apaga o envio de um input num mes (arquivo + apontamentos + xlinks
 * contraparte). Operacao destrutiva, gateada por DEV no backend
 * (``@requires('dev_only')``).
 *
 * @param {Object} args
 * @param {string} args.appName
 * @param {number} args.year
 * @param {number} args.month
 * @param {string} args.metaName
 * @returns {Promise<{
 *   success: true,
 *   meta_name: string,
 *   year: number,
 *   month: number,
 *   deleted_file: string | null,
 *   summary: {
 *     str_notes_deleted: number,
 *     csl_notes_deleted: number,
 *     decisions_superseded: number,
 *     xlinks_reset: number,
 *     blocks_marked_stale: number,
 *     jobs_cancelled: number,
 *   },
 *   cascade: { impacted_blocks: string[], impacted_xlinks: string[] },
 * }>}
 */
export async function deleteInputUploadV3({ appName, year, month, metaName }) {
  const response = await client.post(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/delete-upload/`,
  );
  return response.data;
}

/**
 * Marca um input ``optional`` como concluido sem arquivo (waive).
 * O backend exige ``optional=true`` no catalogo E ausencia de arquivo
 * no periodo (``state='pending'``); senao retorna 400 com
 * ``error_code='waive_not_applicable'``.
 *
 * Para inputs ``yearly``, gravamos o waive no periodo da request; o
 * backend propaga em LEITURA para os demais meses do mesmo ano fiscal
 * (mesma logica de ``has_yearly_success_in_fy``).
 */
export async function setWaiveInputV3({ appName, year, month, metaName }) {
  const response = await client.post(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/waive/`,
  );
  return response.data;
}

/**
 * Marca/desmarca um warning (``severity == 2``) como "analisado".
 *
 * Backend (hibrido): grava ``marked_as_verified`` + autor + data e
 * recomputa o estado denormalizado do control afetado tratando o warning
 * verificado como baixa severidade. A nota crua continua sev=2 (a timeline
 * segue amarela com o carimbo), mas o pip/mes/contadores deixam de
 * registrar o aviso. ``verified=false`` reabre o apontamento (toggle).
 *
 * @returns nota serializada (``_serialize_note``) com
 *   ``marked_as_verified`` / ``marked_as_verified_by`` / ``marked_as_verified_at``.
 */
export async function markInputNoteVerifiedV3({ noteId, verified = true }) {
  const response = await client.post(
    `api/v3/inputs/notes/${encodeURIComponent(noteId)}/mark-verified/`,
    { verified },
  );
  return response.data;
}

/** Inverso de ``setWaiveInputV3``: remove o flag de waive do periodo. */
export async function unsetWaiveInputV3({ appName, year, month, metaName }) {
  const response = await client.delete(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/waive/`,
  );
  return response.data;
}

/**
 * Reinicia a modelagem de um mes inteiro: deleta TODOS os envios
 * nao auto-replicaveis e reseta xlinks/blocos. Operacao destrutiva
 * massiva, gateada por DEV no backend (``@requires('dev_only')``).
 *
 * @param {Object} args
 * @param {string} args.appName
 * @param {number} args.year
 * @param {number} args.month
 * @returns {Promise<{
 *   success: true,
 *   year: number,
 *   month: number,
 *   summary: {
 *     inputs_reset: number,
 *     inputs_kept_auto_reuse: number,
 *     str_notes_deleted: number,
 *     csl_notes_deleted: number,
 *     decisions_superseded: number,
 *     xlinks_reset: number,
 *     blocks_marked_stale: number,
 *     jobs_cancelled: number,
 *   },
 *   kept_auto_reuse_inputs: string[],
 *   reset_inputs: string[],
 * }>}
 */
export async function resetMonthV3({ appName, year, month }) {
  const response = await client.post(
    `api/v3/modeling/${encodeURIComponent(appName)}/${year}/${month}/reset-month/`,
  );
  return response.data;
}

/**
 * Baixa o arquivo bruto de um input V3 (= ``InputControlV3.file``).
 *
 * Espelha ``api/inputs_download/`` do V2, com identidade por
 * ``(app, year, month, meta_name)`` em vez de ``input_control_id``
 * numerico. So suporta ``option='original'`` por enquanto; quando o
 * download do canonical (parquet pos-parse) for plugado, a chave ja
 * esta no contrato.
 *
 * @returns {Promise<{ blob: Blob, filename: string }>}
 *   Stream binario + filename extraido do ``Content-Disposition``. O
 *   chamador costuma passar isso para ``js-file-download`` para
 *   disparar o save-as no browser.
 */
export async function downloadInputV3({
  appName,
  year,
  month,
  metaName,
  option = "original",
}) {
  const response = await fileDownloadClient.post(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/download/`,
    { option },
  );
  // ``Content-Disposition: attachment; filename="<name>"``. Aceita name
  // entre aspas ou sem; cai num default seguro se o header sumir.
  const disp = response.headers["content-disposition"] || "";
  const match = disp.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = match
    ? decodeURIComponent(match[1])
    : `${metaName}_${year}_${month}.bin`;
  return { blob: response.data, filename };
}

/**
 * Busca as notas de checagem (``InputCheckNoteV3``) de um input/mês — usadas
 * pelo drawer (aba *Este mês*) para narrar o que o pipeline V3 observou na
 * estrutura do arquivo (e, quando F.3 ligar, também nos cruzamentos).
 *
 * Shape de resposta:
 *   {
 *     meta_name, year, month,
 *     control: { state, delivered_at, original_filename } | null,
 *     notes: [
 *       {
 *         id, check_type ("STR"|"CSL"), severity (1|2|3),
 *         check_description: string | {en, pt, es, ...},
 *         notes: string | {en, pt, es, ...},
 *         associated_values: any | null,
 *         marked_as_verified: bool,
 *         created_at, job_id
 *       }
 *     ]
 *   }
 *
 * Se não houver ``InputControlV3`` no mês, ``control=null`` e ``notes=[]`` —
 * a UI mostra o estado "nada enviado ainda" sem ramo de erro extra.
 */
/**
 * Lista blocos de calculo (BlockV3) da implementacao corrente com o
 * estado vivo (BlockControlV3) do mes selecionado.
 *
 * Consumido pelo ``BlocksPane`` do ``ProgressDrawerV3``. Backend:
 * ``InputsV3BlocksListView``.
 *
 * Shape:
 *   {
 *     blocks: [{ id, metaName, displayName, description, kind,
 *                pnlLines, isStub, status, duration, lastRun,
 *                lastJobId, outputPaths, maxSeverity, error }],
 *     summary: { done, running, waiting, failed, stale },
 *     snapshotSource: "local" | "blob" | null
 *   }
 */
export async function fetchBlocksV3({ year, month, etag } = {}) {
  // ``If-None-Match`` (PA-009): quando o client passa um ``etag``
  // previo, o backend devolve 304 sem corpo se nada mudou no mes.
  // Retornamos sempre um shape consistente: ``{ data, etag,
  // notModified }``. Quando 304, ``data`` vira ``null`` (cabe ao
  // chamador preservar o estado anterior).
  const headers = {};
  if (etag) headers["If-None-Match"] = etag;
  const response = await client.get("api/v3/blocks/", {
    params: { year, month },
    headers,
    // ``validateStatus``: 304 nao pode disparar throw — e a sinalizacao
    // padrao "use seu cache". axios o trata como erro por default.
    validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
  });
  if (response.status === 304) {
    return {
      data: null,
      etag: response.headers?.etag || etag || null,
      notModified: true,
    };
  }
  return {
    data: response.data,
    etag: response.headers?.etag || null,
    notModified: false,
  };
}

export async function fetchBlockLogV3({ metaName, year, month }) {
  const response = await client.get(
    `api/v3/blocks/${encodeURIComponent(metaName)}/log/`,
    { params: { year, month } },
  );
  return response.data;
}

export async function fetchBlockLogIndicatorSeriesV3({
  metaName,
  indicatorKey,
}) {
  const response = await client.get(
    `api/v3/blocks/${encodeURIComponent(metaName)}/log/indicators/${encodeURIComponent(
      indicatorKey,
    )}/`,
  );
  return response.data;
}

export async function fetchBlockLogMeasureSeriesBatchV3({ metaName, keys }) {
  const response = await client.post(
    `api/v3/blocks/${encodeURIComponent(metaName)}/log/measures/series/`,
    { keys },
  );
  return response.data;
}

export async function downloadBlockLogFileV3({ logId, filename }) {
  const response = await fileDownloadClient.get(
    `api/v3/block-logs/${encodeURIComponent(logId)}/downloads/${encodeURIComponent(
      filename,
    )}/`,
  );
  const disp = response.headers["content-disposition"] || "";
  const match = disp.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const resolvedFilename = match ? decodeURIComponent(match[1]) : filename;
  return { blob: response.data, filename: resolvedFilename };
}

/**
 * Endpoint consolidado do ``ProgressDrawerV3``.
 *
 * Backend: ``InputsV3ProgressOverviewView`` — 1 chamada serve cockpit
 * (KPIs/progress), pendencias, linha do tempo e status do mes (badge
 * principal). Polling no front e a cada 10s.
 *
 * Shape (resumido — ver backend para detalhes):
 *   {
 *     period: { year, month, auto_dispatch },
 *     month_status: "complete|processing|stale|ready_to_calculate|error|waiting",
 *     month_aggregates: [{ year, month, status }],
 *     months_implementation: { done, stale, processing, total },
 *     actions_count: { decisions, uploads, reenvios, errors, recalcs },
 *     queue: { running, queued, waiting_inputs },
 *     progress: { done, stale, action, waiting },
 *     events: [...],
 *     actions: [...],
 *     userIsDev: bool
 *   }
 */
export async function fetchProgressOverviewV3({ year, month, etag } = {}) {
  const headers = {};
  if (etag) headers["If-None-Match"] = etag;
  const response = await client.get("api/v3/progress_overview/", {
    params: { year, month },
    headers,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
  });
  if (response.status === 304) {
    return {
      data: null,
      etag: response.headers?.etag || etag || null,
      notModified: true,
    };
  }
  return {
    data: response.data,
    etag: response.headers?.etag || null,
    notModified: false,
  };
}

/**
 * Recoloca um bloco V3 na fila do scheduler (DEV-only).
 * Backend: ``InputsV3DispatchBlockView`` em
 * ``POST /api/v3/blocks/<meta_name>/dispatch/``.
 *
 * Semantica: "volte para a fila", nao "force run". A view faz triagem
 * de prontidao (leva 98) antes de gravar:
 *
 * - Deps prontas → ``state="pending"`` (UI "na fila") + signal scheduler.
 * - Deps faltando → ``state="blocked"`` (UI "aguardando"); sem signal
 *   (nao ha o que despachar). Bloco volta a ser candidato natural
 *   quando algum input/upstream mudar.
 *
 * Resposta::
 *
 *     {
 *       queued: boolean,            // true se foi pra fila
 *       block_control_id: number,
 *       new_state: "pending" | "blocked",
 *       new_status: "stale" | "waiting",  // vocabulario da UI
 *       missing_deps: string[],     // [] quando queued=true
 *       downstream_count: 0,        // legado (sempre 0 desde PA-021)
 *     }
 *
 * Erros relevantes:
 * - 403 ``forbidden_dispatch_block`` — usuario nao e DEV.
 * - 404 ``block_not_in_snapshot`` — bloco ausente do snapshot atual.
 * - 409 ``block_already_running`` — ja existe job ativo para este
 *   bloco/mes; novo clique e' redundante.
 */
export async function dispatchBlockV3({ metaName, year, month }) {
  const response = await client.post(
    `api/v3/blocks/${encodeURIComponent(metaName)}/dispatch/`,
    { year, month },
  );
  return response.data;
}

/**
 * Endpoint consolidado do ``InputDrawerV3``.
 *
 * Backend: ``InputsV3DrawerDetailsView``
 *   GET /api/v3/inputs/<app>/<year>/<month>/<meta>/drawer-details/
 *
 * Substitui o conjunto fragmentado (notes, decisions, cross_links,
 * history, about/reuse_config, audit_recent) por uma unica chamada,
 * com objetivo declarado de <=4 queries SQL no backend (PA-007). O
 * front consome polling 5s enquanto o ``control.state`` for
 * ``uploaded``/``processing``; em estados terminais o polling para.
 *
 * Shape (resumido — ver ``api/views/inputs_v3.py`` para detalhes):
 *   {
 *     meta_name, year, month,
 *     about: { display_name, description, instructions_md, periodicity?,
 *              expected_file_type, volatility, reuse_allowed, reuse_auto,
 *              responsibles: [...] },
 *     control: { state, delivered_at, original_filename,
 *                file_size, updated_by, ... } | null,
 *     current_month_uploads: [
 *       { id, original_filename, delivered_at, updated_by, file_size,
 *         state }
 *     ],
 *     previous_month_upload: {
 *       year, month, original_filename, delivered_at, updated_by,
 *       file_size, state
 *     } | null,
 *     notes: [...],
 *     answered_decisions: [...],
 *     decisions: [...],
 *     cross_links: [...],
 *     audit_recent: [
 *       { field_name, old_value, new_value, changed_by, changed_at }
 *     ]
 *   }
 */
export async function fetchInputDrawerDetailsV3({
  appName,
  year,
  month,
  metaName,
}) {
  const response = await client.get(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/drawer-details/`,
  );
  return response.data;
}

/**
 * PATCH dos toggles de reuso (``reuse_allowed``/``reuse_auto``) do
 * ``ImplementationInputV3``. Backend:
 *   PATCH /api/v3/inputs/<meta_name>/reuse-config/
 *
 * Apenas DEV/IMG/CAD podem alterar (DA-003). O backend grava entrada
 * em ``ImplementationInputV3FieldChange`` para auditoria.
 *
 * Body aceito:
 *   { reuse_allowed?: bool, reuse_auto?: bool }
 *
 * Erros usuais:
 *   - 403 ``forbidden_reuse_config``
 *   - 400 ``reuse_auto_requires_reuse_allowed``
 */
export async function saveInputReuseConfigV3({
  metaName,
  reuseAllowed,
  reuseAuto,
}) {
  const payload = {};
  if (reuseAllowed !== undefined) payload.reuse_allowed = !!reuseAllowed;
  if (reuseAuto !== undefined) payload.reuse_auto = !!reuseAuto;
  const response = await client.patch(
    `api/v3/inputs/${encodeURIComponent(metaName)}/reuse-config/`,
    payload,
  );
  return response.data;
}

/**
 * Linha do tempo cronologica do mes (uploads, parsings, decisoes,
 * runs de bloco). Substitui os mocks da ``TimelinePane``.
 *
 * Backend: ``InputsV3TimelineView``
 *   GET /api/v3/timeline/?year=&month=
 */
export async function fetchInputsV3Timeline({ year, month }) {
  const response = await client.get("api/v3/timeline/", {
    params: { year, month },
  });
  return response.data;
}

export async function fetchInputNotesV3({ appName, year, month, metaName }) {
  const response = await client.get(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/notes/`,
  );
  return response.data;
}

/**
 * Lista cross-links em que um input participa, com status do mes.
 *
 * Consumido pela aba ``Cruzamentos`` do drawer (``CrossLinksPane``).
 * Backend: ``InputsV3InputCrossLinksView``.
 *
 * Shape:
 *   {
 *     meta_name, year, month,
 *     cross_links: [
 *       {
 *         meta_name, display_name, description,
 *         role: "fact" | "dimension" | "bridge" | string[] | null,
 *         key_columns: string[],
 *         measure_column: string | null,
 *         threshold: { max_ratio?, max_absolute?, within_tolerance_ratio?, ... },
 *         other_participants: [
 *           { input_meta_name, display_name, role, key_columns, alias }
 *         ],
 *         severity_on_failure, severity_on_pending,
 *         control: { state, max_severity, last_run_at } | null,
 *         evaluation: {
 *           evaluated, coverage_pct, missing_ratio, state, year, month,
 *           last_run_at
 *         },
 *         history: [ { year, month, coverage_pct, state, last_run_at } ]
 *       }
 *     ]
 *   }
 */
export async function fetchInputCrossLinksV3({
  appName,
  year,
  month,
  metaName,
}) {
  const response = await client.get(
    `api/v3/inputs/${encodeURIComponent(appName)}/${year}/${month}/${encodeURIComponent(
      metaName,
    )}/cross-links/`,
  );
  return response.data;
}

/**
 * PATCH dos overrides de threshold de um cross-link da implementacao.
 *
 * Frente Severity & Thresholds — calibragem dos limites de divergencia
 * (atencao / critico) sem redeploy do descriptor. ``xlinkId`` e o
 * ``id`` do ``ImplementationCrossLinkV3`` (exposto pelo GET acima
 * como ``cross_links[*].id``).
 *
 * Payload aceito (cada campo e opcional; ``null`` explicito limpa o
 * override correspondente):
 *   {
 *     attention_threshold_override: number | null,
 *     critical_threshold_override:  number | null,
 *     apply_retroactively:          boolean
 *   }
 *
 * Erros usuais (``error_code``):
 *   - ``forbidden_threshold_override`` — papel sem permissao e usuario
 *     nao e responsavel por nenhum input do cross-link.
 *   - ``critical_must_be_above_attention`` — coerencia dos limites.
 *   - ``*_out_of_range`` / ``*_must_be_float_or_null`` — validacao.
 */
/**
 * Salva instrucoes (markdown) de um input. Apenas DEV/IMG.
 * Backend: PATCH /api/v3/inputs/<meta_name>/instructions/.
 */
export async function saveInputInstructionsV3({ metaName, instructionsMd }) {
  const response = await client.patch(
    `api/v3/inputs/${encodeURIComponent(metaName)}/instructions/`,
    { instructions_md: instructionsMd ?? null },
  );
  return response.data;
}

/**
 * Gera sugestão de instruções (markdown) a partir do descritor.
 * Não persiste. Apenas DEV/IMG.
 * Backend: POST /api/v3/inputs/<meta_name>/instructions/suggest/.
 */
export async function suggestInputInstructionsV3({ metaName }) {
  const response = await client.post(
    `api/v3/inputs/${encodeURIComponent(metaName)}/instructions/suggest/`,
  );
  return response.data;
}

export async function saveCrossLinkOverridesV3({
  xlinkId,
  attentionRatio,
  criticalRatio,
  applyRetroactively,
  // Onda 3.2 (mai/2026): novos controles editaveis no
  // ThresholdEditor. ``onCriticalSeverity`` = 'ask' | 'fail';
  // ``authoritativeMetaNames`` = lista canonica (foto inteira) dos
  // meta_names que devem ser ``is_authoritative=true``. Ambos
  // opcionais — se omitidos, o backend nao mexe nos campos.
  onCriticalSeverity,
  authoritativeMetaNames,
}) {
  const payload = { apply_retroactively: !!applyRetroactively };
  if (attentionRatio !== undefined) {
    payload.attention_threshold_override = attentionRatio;
  }
  if (criticalRatio !== undefined) {
    payload.critical_threshold_override = criticalRatio;
  }
  if (onCriticalSeverity !== undefined) {
    payload.on_critical_severity = onCriticalSeverity;
  }
  if (authoritativeMetaNames !== undefined) {
    payload.authoritative_meta_names = authoritativeMetaNames;
  }
  const response = await client.patch(
    `api/v3/implementation-cross-links/${xlinkId}/overrides/`,
    payload,
  );
  return response.data;
}

/**
 * Lista decisoes pendentes (``InputDecisionV3``) da implementacao ativa.
 *
 * Alimenta o sino global de notificacao pervasiva (``P-E`` / ``D-22``). Sem
 * ``year``/``month``, devolve TODAS as decisoes pendentes (cross-periodo) —
 * o contador do sino faz sentido como agregado.
 *
 * Response:
 *   {
 *     implementation: { app_name, display_name },
 *     period: { year, month } | null,
 *     count: number,
 *     decisions: [
 *       { id, scope, meta_name, year, month, question_md,
 *         options: [...], default_suggestion_index, source_event,
 *         state, created_at }
 *     ]
 *   }
 *
 * Respostas comuns:
 *   - ``403 inputs_v3_not_enabled`` — implementacao ativa nao esta na
 *     allowlist da V3. O consumidor (sino) deve tratar como "zero
 *     pendencias" silenciosamente, nao como erro de UI.
 */
export async function fetchInputDecisionsV3({ year, month, scope } = {}) {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  if (scope) params.scope = scope;
  const response = await client.get("api/v3/input-decisions/", { params });
  return response.data;
}

/**
 * Resolve (responde) uma ``InputDecisionV3`` pendente.
 *
 * Envia ``POST /api/v3/input-decisions/<decisionId>/answer/`` com o
 * indice da opcao escolhida e o flag opcional de "lembrar para os
 * proximos meses" (cache semantico, P-25 reabertura futura).
 *
 * Contrato (consistente com ``InputsV3DecisionAnswerView`` no backend):
 *
 *   request:
 *     {
 *       chosen_index: number,   // obrigatorio, 0 <= i < options.length
 *       remember_for_future?: boolean // default false
 *     }
 *
 *   response 200:
 *     {
 *       id, state: "answered", chosen_index, answered_at,
 *       answered_by: { username, email } | null,
 *       remember_for_future
 *     }
 *
 *   erros tratados pelo chamador:
 *     - 400 invalid_chosen_index
 *     - 404 decision_not_found
 *     - 409 decision_already_answered (alguem respondeu em paralelo)
 *
 * O chamador deve re-buscar a lista de decisoes apos sucesso (o card
 * do drawer e o sino global do header sao fontes separadas; cada um
 * controla seu refetch).
 */
export async function answerInputDecisionV3(
  decisionId,
  { chosenIndex, rememberForFuture = false } = {},
) {
  const response = await client.post(
    `api/v3/input-decisions/${decisionId}/answer/`,
    {
      chosen_index: chosenIndex,
      remember_for_future: rememberForFuture,
    },
  );
  return response.data;
}
